import React from 'react';

/**
 * FT-353e — post-completion "Close linked defect?" prompt.
 *
 * Rendered by MaintenanceModule after a corrective maintenance job completes
 * and the completion links back to a defect that is still Open / In Progress.
 * Per advisor decision #2 (2026-07-09), we NEVER auto-close a defect — the
 * user must confirm. ISM sign-off = human decision.
 *
 * Props:
 *   defect          — the defect record (must have id + description|title)
 *   completedJobId  — the id of the corrective job that just completed (for
 *                     the audit history note)
 *   onCloseDefect(defect, completedJobId) — user clicked [Close defect]
 *   onKeepOpen()    — user clicked [Not yet]
 */
export default function CloseLinkedDefectPrompt({
  defect,
  completedJobId,
  onCloseDefect,
  onKeepOpen,
}) {
  if (!defect) return null;

  const shortId = String(defect.id).slice(-5);
  const label = defect.description || defect.title || 'No description';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      data-testid="ft353e-close-defect-prompt"
    >
      <div className="bg-white rounded-lg p-6 max-w-md mx-4 shadow-xl">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          Corrective job completed. Close linked defect?
        </h3>
        <p className="text-sm text-gray-700 mb-2">
          Defect DEF-{shortId}: {label}
        </p>
        <p className="text-xs text-gray-500 mb-6">
          Marking this defect as Closed indicates the corrective action has
          resolved the issue.
        </p>
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={onKeepOpen}
            data-testid="ft353e-not-yet"
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Not yet
          </button>
          <button
            type="button"
            onClick={() => onCloseDefect(defect, completedJobId)}
            data-testid="ft353e-close-defect"
            className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700"
          >
            Close defect
          </button>
        </div>
      </div>
    </div>
  );
}
